import React, { useState } from 'react';
import { Card, Skeleton, Empty, Space, Typography } from 'antd';
import { Line } from '@ant-design/plots';
import dayjs from 'dayjs';
import { useGlobalState } from '../../hooks/useGlobalState.js';

const colors = {
  users: '#1677ff',
  feedbacks: '#52c41a',
  ctas: '#ff7a45',
  conversations: '#722ed1',
};

export default function Trends({ loading, data, mode, onModeChange }) {
  const { userProfile } = useGlobalState();
  const [hiddenSeries, setHiddenSeries] = useState([]);

  const seriesDefs = [
    { id: 'Users', key: 'users', color: colors.users },
    userProfile?.user?.userStatus !== "Company User" && { id: 'Feedback', key: 'feedbacks', color: colors.feedbacks },
    { id: 'CTA', key: 'ctas', color: colors.ctas },
    { id: 'Conversation', key: 'conversations', color: colors.conversations },
  ].filter(Boolean);

  // Filter data based on hidden status
  const filteredData = seriesDefs
    .flatMap((s) =>
      !hiddenSeries.includes(s.id) ? (data?.[s.key] || []).map((d) => ({
        date: d?.date,
        value: Number(d?.value),
        category: s.id,
      })) : []
    )
    .filter((pt) => typeof pt.date === 'string' && pt.date && Number.isFinite(pt.value));

  const hasData = filteredData.length >= 2;

  // Click on the legend for processing
  const handleLegendClick = (category) => {
    setHiddenSeries(prev => {
      if (prev.includes(category)) {
        // If it is already hidden, display it
        return prev.filter(item => item !== category);
      } else {
        // If currently displayed, hide
        return [...prev, category];
      }
    });
  };

  // Custom Legend Component
  const legend = (
    <Space size={16} wrap>
      {seriesDefs.map((s) => (
        <Space 
          key={s.id} 
          size={6} 
          style={{ cursor: 'pointer', opacity: hiddenSeries.includes(s.id) ? 0.4 : 1 }}
          onClick={() => handleLegendClick(s.id)}
        >
          <span 
            style={{ 
              width: 10, 
              height: 10, 
              background: s.color, 
              display: 'inline-block', 
              borderRadius: 2 
            }} 
          />
          <span style={{ color: '#595959', fontSize: 13 }}>
            {s.id}
          </span>
        </Space>
      ))}
    </Space>
  );

  const config = {
    data: filteredData,
    xField: (d) => new Date(`${d.date}T00:00:00`),
    yField: 'value',
    seriesField: 'category',
    colorField: 'category',
    color: seriesDefs.map(s => s.color),
    scale: {
      color: {
        domain: seriesDefs.map(s => s.id),
        range: seriesDefs.map(s => s.color),
      },
    },
    axis: { x: { title: false, size: 40 }, y: { title: false, size: 36 } },
    slider: {
      x: { labelFormatter: (d) => dayjs(d).format('YYYY/M/D') },
      y: { labelFormatter: (v) => String(v) },
    },
    legend: false,
    point: false,
    tooltip: {
      shared: true,
      showMarkers: true,
      title: 'date',
    },
  };

  return (
    <Card title={
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <Space size={12}>
          <Typography.Text
            onClick={() => onModeChange?.('daily')}
            style={{ cursor: 'pointer', color: mode === 'daily' ? '#1677ff' : '#1f1f1f' }}
          >
            Daily Stats
          </Typography.Text>
          <span style={{ color: '#d9d9d9' }}>|</span>
          <Typography.Text
            onClick={() => onModeChange?.('cumulative')}
            style={{ cursor: 'pointer', color: mode === 'cumulative' ? '#1677ff' : '#1f1f1f' }}
          >
            Cumulative Stats
          </Typography.Text>
        </Space>
        {legend}
      </div>
    }>
      {loading ? (
        <Skeleton active />
      ) : !hasData ? (
        <Empty />
      ) : (
        <div style={{ height: 340 }}>
          <Line {...config} />
        </div>
      )}
    </Card>
  );
}
